import { getItem, setItem } from '../../services/localStorageService';
import { LOCAL_STORAGE_KEYS } from '../../consts/constants';
import {
  DEFAULT_BRAND_KEY,
  getBrandConfig,
} from './registry';
import {
  isHaleLoginPreview,
  isHaleUrl,
  isLocalDevelopment,
  isLocalHaleSession,
  resolveUserBuId,
} from './brandEnvironment';
import { BrandKey } from './types';

const isAuthenticated = (): boolean =>
  Boolean(
    getItem(LOCAL_STORAGE_KEYS.AUTH_KEY) ||
      getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN),
  );

const getStoredUserBuId = (): number | null => {
  const stored = getItem(LOCAL_STORAGE_KEYS.USER_BU_ID);
  if (stored == null || stored === '') return null;
  const parsed = Number(stored);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Theme from URL/host only — not user buId.
 * Hale: hale.sparkl.ac / hale.sparkl.me, or localhost ?hale session.
 */
const resolveThemeBrandKey = (): BrandKey => {
  if (isLocalDevelopment() && isLocalHaleSession()) {
    return 'hale';
  }

  if (isHaleUrl()) {
    return 'hale';
  }

  return DEFAULT_BRAND_KEY;
};

/** @deprecated Theme is URL-based; buId is ignored. */
export const resolveBrandFromBuId = (_buId?: number): BrandKey =>
  resolveThemeBrandKey();

/**
 * Post-login theme: hale.* domain → Hale, else student.
 * Localhost uses ?hale session flag for local QA.
 */
export const resolveAuthenticatedBrandKey = (): BrandKey =>
  resolveThemeBrandKey();

/**
 * Pre-login: Hale host or localhost/login?hale. Post-login: domain/session only.
 */
export const resolveBrandKey = (): BrandKey => {
  if (isAuthenticated()) {
    return resolveAuthenticatedBrandKey();
  }

  if (isHaleLoginPreview()) {
    return 'hale';
  }

  return DEFAULT_BRAND_KEY;
};

/** Hale login when URL is hale.sparkl.ac/.me, or localhost/login?hale */
export const shouldShowHaleLoginPage = (): boolean => isHaleLoginPreview();

let activeBrandKey: BrandKey = DEFAULT_BRAND_KEY;

export const getActiveBrandKey = (): BrandKey => activeBrandKey;

export const getActiveBrandConfig = () => getBrandConfig(activeBrandKey);

export const setActiveBrandKey = (key: BrandKey): void => {
  activeBrandKey = key;
};

export const initActiveBrand = (): BrandKey => {
  activeBrandKey = resolveBrandKey();
  return activeBrandKey;
};

/** Persists user buId from API; theme follows domain only. */
export const reconcileBrandFromUserBuId = (
  apiBuId?: number | string | null,
): BrandKey => {
  const storedBuId = getStoredUserBuId();
  const fromHaleContext =
    isHaleUrl() || (isLocalDevelopment() && isLocalHaleSession());

  const resolvedBuId = resolveUserBuId(apiBuId ?? storedBuId, {
    fromHaleContext,
  });
  setItem(LOCAL_STORAGE_KEYS.USER_BU_ID, String(resolvedBuId));
  activeBrandKey = resolveAuthenticatedBrandKey();
  return activeBrandKey;
};

/** @deprecated Theme is URL-based. */
export const resolveBrandAfterLogin = (options: {
  fromHaleLoginPage: boolean;
  userBuId?: number | null;
}): BrandKey => {
  void options;
  return resolveThemeBrandKey();
};
